/**
 * IndexedDB utility for persisting asset blobs across sessions
 * Stores blob data in IndexedDB, allowing assets to persist across app restarts
 */

const DB_NAME = 'dreambooth-asset-cache'
const DB_VERSION = 1
const STORE_NAME = 'assets'

interface CachedAsset {
  url: string
  blob: Blob
  timestamp: number
  size: number
}

let dbInstance: IDBDatabase | null = null

/**
 * Open or get the IndexedDB database instance
 */
const getDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (): void => {
      reject(new Error('Failed to open IndexedDB'))
    }

    request.onsuccess = (): void => {
      dbInstance = request.result
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'url' })
        objectStore.createIndex('timestamp', 'timestamp', { unique: false })
        objectStore.createIndex('size', 'size', { unique: false })
      }
    }
  })
}

/**
 * Get a cached blob from IndexedDB
 * @param url - The URL of the asset
 * @returns Promise resolving to the Blob or null if not found
 */
export const getCachedBlob = async (url: string): Promise<Blob | null> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(url)

      request.onerror = (): void => {
        reject(new Error('Failed to get cached blob'))
      }

      request.onsuccess = (): void => {
        const result = request.result as CachedAsset | undefined
        if (result && result.blob) {
          resolve(result.blob)
        } else {
          resolve(null)
        }
      }
    })
  } catch (error) {
    console.error('Error getting cached blob:', error)
    return null
  }
}

/**
 * Remove a cached blob from IndexedDB (e.g. when blob is invalid/poisoned)
 * @param url - The URL of the asset to evict
 * @returns Promise that resolves when the entry is removed
 */
export const deleteCachedBlob = async (url: string): Promise<void> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(url)

      request.onerror = (): void => {
        reject(new Error('Failed to delete cached blob'))
      }

      request.onsuccess = (): void => {
        resolve()
      }
    })
  } catch (error) {
    console.error('Error deleting cached blob:', error)
    throw error
  }
}

/**
 * Store a blob in IndexedDB
 * @param url - The URL of the asset
 * @param blob - The blob data to store
 * @returns Promise that resolves when the blob is stored
 */
export const setCachedBlob = async (url: string, blob: Blob): Promise<void> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const cachedAsset: CachedAsset = {
        url,
        blob,
        timestamp: Date.now(),
        size: blob.size
      }

      const request = store.put(cachedAsset)

      request.onerror = (): void => {
        // Handle quota exceeded gracefully
        if (request.error?.name === 'QuotaExceededError') {
          console.warn('IndexedDB quota exceeded, attempting to clear expired entries')
          clearExpired(24)
            .then(() => {
              // Retry after clearing expired
              const retryRequest = store.put(cachedAsset)
              retryRequest.onsuccess = (): void => resolve()
              retryRequest.onerror = (): void => {
                console.error('Failed to store blob after clearing expired:', retryRequest.error)
                reject(retryRequest.error)
              }
            })
            .catch((clearError) => {
              console.error('Failed to clear expired entries:', clearError)
              reject(request.error)
            })
        } else {
          reject(request.error)
        }
      }

      request.onsuccess = (): void => {
        resolve()
      }
    })
  } catch (error) {
    console.error('Error setting cached blob:', error)
    throw error
  }
}

/**
 * Clear expired entries from IndexedDB
 * @param maxAgeHours - Maximum age in hours (default: 24)
 * @returns Promise that resolves when expired entries are cleared
 */
export const clearExpired = async (maxAgeHours: number = 24): Promise<void> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('timestamp')
      const maxAge = Date.now() - maxAgeHours * 60 * 60 * 1000

      const request = index.openCursor(IDBKeyRange.upperBound(maxAge))

      request.onerror = (): void => {
        reject(new Error('Failed to clear expired entries'))
      }

      request.onsuccess = (event: Event): void => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        } else {
          resolve()
        }
      }
    })
  } catch (error) {
    console.error('Error clearing expired entries:', error)
    throw error
  }
}

/**
 * Clear all entries from IndexedDB
 * @returns Promise that resolves when all entries are cleared
 */
export const clearAll = async (): Promise<void> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onerror = (): void => {
        reject(new Error('Failed to clear all entries'))
      }

      request.onsuccess = (): void => {
        resolve()
      }
    })
  } catch (error) {
    console.error('Error clearing all entries:', error)
    throw error
  }
}

/**
 * Get all cached URLs (for debugging/monitoring)
 * @returns Promise resolving to array of cached URLs
 */
export const getAllCachedUrls = async (): Promise<string[]> => {
  try {
    const db = await getDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAllKeys()

      request.onerror = (): void => {
        reject(new Error('Failed to get cached URLs'))
      }

      request.onsuccess = (): void => {
        resolve(request.result as string[])
      }
    })
  } catch (error) {
    console.error('Error getting cached URLs:', error)
    return []
  }
}
